import React, {
  useEffect,
  useReducer,
  useState,
  useContext,
  useMemo,
} from "react";

import {
  Button,
  IconButton,
  makeStyles,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@material-ui/core";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import Title from "../../components/Title";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import { DeleteOutline, Edit } from "@material-ui/icons";
import QueueModal from "../../components/QueueModal";
import { toast } from "react-toastify";
import ConfirmationModal from "../../components/ConfirmationModal";
import { SocketContext } from "../../context/Socket/SocketContext";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  customTableCell: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
}));

const normalizeQueue = (queue) => {
  const normalizedId =
    queue.id !== undefined && queue.id !== null
      ? String(queue.id)
      : queue.id;

  const normalizedParentId =
    queue.parentId !== undefined && queue.parentId !== null
      ? String(queue.parentId)
      : null;

  return {
    ...queue,
    id: normalizedId,
    parentId: normalizedParentId,
    displayCode: queue.displayCode || `${normalizedId}`,
    level: queue.level || 1,
  };
};

const reducer = (state, action) => {
  if (action.type === "LOAD_QUEUES") {
    const queues = action.payload.map(normalizeQueue);
    const mergedQueues = [...state];

    queues.forEach((queue) => {
      const queueIndex = mergedQueues.findIndex((q) => q.id === queue.id);
      if (queueIndex !== -1) {
        mergedQueues[queueIndex] = queue;
      } else {
        mergedQueues.push(queue);
      }
    });

    return mergedQueues;
  }

  if (action.type === "UPDATE_QUEUES") {
    const queue = normalizeQueue(action.payload);
    const queueIndex = state.findIndex((u) => u.id === queue.id);

    if (queueIndex !== -1) {
      state[queueIndex] = queue;
      return [...state];
    } else {
      return [queue, ...state];
    }
  }

  if (action.type === "DELETE_QUEUE") {
    const queueId = action.payload;
    const queueIndex = state.findIndex((q) => q.id === queueId);
    if (queueIndex !== -1) {
      state.splice(queueIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const Queues = () => {
  const classes = useStyles();

  const [queues, dispatch] = useReducer(reducer, []);
  const [loading, setLoading] = useState(false);

  const [queueModalOpen, setQueueModalOpen] = useState(false);
  const [selectedQueue, setSelectedQueue] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);

  const socketManager = useContext(SocketContext);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/queue");
        dispatch({ type: "LOAD_QUEUES", payload: data });

        setLoading(false);
      } catch (err) {
        toastError(err);
        setLoading(false);
      }
    })();
  }, []);

  const queueTree = useMemo(() => {
    const queueMap = new Map(
       queues.map((queue) => [String(queue.id), { ...queue, children: [] }])
    );

    queueMap.forEach((queue) => {
      if (queue.parentId && queueMap.has(String(queue.parentId))) {
        const parent = queueMap.get(String(queue.parentId));
        parent.children.push(queue);
      }
    });

    const sortQueues = (list) =>
      list
        .map((item) => ({
          ...item,
          children: sortQueues(item.children || []),
        }))
        .sort((a, b) => {
          const aOrder = a.orderQueue ?? 0;
          const bOrder = b.orderQueue ?? 0;

          if (aOrder !== bOrder) {
            return aOrder - bOrder;
          }

          return String(a.displayCode).localeCompare(String(b.displayCode));
        });

    const roots = Array.from(queueMap.values()).filter(
      (queue) => !queue.parentId || !queueMap.has(queue.parentId)
    );

    return sortQueues(roots);
  }, [queues]);

  const renderQueueRows = (queueList, depth = 0) => {
    return queueList.flatMap((queue) => [
      <TableRow key={queue.id}>
        <TableCell align="center">{queue.displayCode}</TableCell>
        <TableCell align="left">
          <div style={{ paddingLeft: depth * 16, display: "flex" }}>
            <Typography noWrap variant="body2">
              {queue.name}
            </Typography>
          </div>
        </TableCell>
        <TableCell align="center">
          <div className={classes.customTableCell}>
            <span
              style={{
                backgroundColor: queue.color,
                width: 60,
                height: 20,
                alignSelf: "center",
              }}
            />
          </div>
        </TableCell>
        <TableCell align="center">
          <div className={classes.customTableCell}>
            <Typography
              style={{ width: 300, align: "center" }}
              noWrap
              variant="body2"
            >
              {queue.orderQueue}
            </Typography>
          </div>
        </TableCell>
        <TableCell align="center">
          <div className={classes.customTableCell}>
            <Typography
              style={{ width: 300, align: "center" }}
              noWrap
              variant="body2"
            >
              {queue.greetingMessage}
            </Typography>
          </div>
        </TableCell>
        <TableCell align="center">
          <IconButton size="small" onClick={() => handleEditQueue(queue)}>
            <Edit />
          </IconButton>

          <IconButton
            size="small"
            onClick={() => {
              setSelectedQueue(queue);
              setConfirmModalOpen(true);
            }}
          >
            <DeleteOutline />
          </IconButton>
        </TableCell>
      </TableRow>,
      ...renderQueueRows(queue.children || [], depth + 1),
    ]);
  };

  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    const socket = socketManager.getSocket(companyId);

    socket.on(`company-${companyId}-queue`, (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_QUEUES", payload: data.queue });
      }

      if (data.action === "delete") {
        dispatch({ type: "DELETE_QUEUE", payload: data.queueId });
      }
    });

    return () => {
      socket.disconnect();
    };
  }, [socketManager]);

  const handleOpenQueueModal = () => {
    setQueueModalOpen(true);
    setSelectedQueue(null);
  };

  const handleCloseQueueModal = () => {
    setQueueModalOpen(false);
    setSelectedQueue(null);
  };

  const handleEditQueue = (queue) => {
    setSelectedQueue(queue);
    setQueueModalOpen(true);
  };

  const handleCloseConfirmationModal = () => {
    setConfirmModalOpen(false);
    setSelectedQueue(null);
  };

  const handleDeleteQueue = async (queueId) => {
    try {
      await api.delete(`/queue/${queueId}`);
      toast.success(i18n.t("queues.toasts.success"));
    } catch (err) {
      toastError(err);
    }
    setSelectedQueue(null);
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title={
          selectedQueue &&
          `${i18n.t("queues.confirmationModal.deleteTitle")} ${
            selectedQueue.name
          }?`
        }
        open={confirmModalOpen}
        onClose={handleCloseConfirmationModal}
        onConfirm={() => handleDeleteQueue(selectedQueue.id)}
      >
        {i18n.t("queues.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <QueueModal
        open={queueModalOpen}
        onClose={handleCloseQueueModal}
        queueId={selectedQueue?.id}
      />
      <MainHeader>
        <Title>{i18n.t("queues.title")}</Title>
        <MainHeaderButtonsWrapper>
          <Button
            variant="contained"
            color="primary"
            onClick={handleOpenQueueModal}
          >
            {i18n.t("queues.buttons.add")}
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>
      <Paper className={classes.mainPaper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell align="center">{i18n.t("queues.table.id")}</TableCell>
              <TableCell align="center">
                {i18n.t("queues.table.name")}
              </TableCell>
              <TableCell align="center">
                {i18n.t("queues.table.color")}
              </TableCell>
              <TableCell align="center">
                {i18n.t("queues.table.orderQueue")}
              </TableCell>
              <TableCell align="center">
                {i18n.t("queues.table.greeting")}
              </TableCell>
              <TableCell align="center">
                {i18n.t("queues.table.actions")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <>
              {renderQueueRows(queueTree)}
              {loading && <TableRowSkeleton columns={6} />}
            </>
          </TableBody>
        </Table>
      </Paper>
    </MainContainer>
  );
};

export default Queues;
